import type { HealthcareService, Reference } from '@medplum/fhirtypes'
import * as healthcareServices from '@seen/core/models/healthcareService/index'
import { getReferenceString } from '@seen/core/references'
import { Button } from '@seen/ui'
import { type ComponentProps, type JSX, useMemo } from 'react'
import {
  Combobox,
  type ComboboxOption,
} from '#src/components/core/combobox/Combobox.tsx'
import { useSearch } from '#src/hooks/useSearch.ts'

export function HealthcareServiceComboboxButton({
  value,
  onChange,
  ...delegated
}: {
  value?: Reference<HealthcareService>
  onChange: (value: Reference<HealthcareService>) => void
} & Omit<ComponentProps<typeof Button>, 'onChange' | 'value'>): JSX.Element {
  const { data } = useSearch(
    healthcareServices.ExtendedHealthcareService,
    healthcareServices.ExtendedHealthcareService.baseSearchQuery()
  )
  const healthcareServicesList = useMemo(() => data ?? [], [data])

  const options: ComboboxOption<string>[] = useMemo(
    () =>
      healthcareServicesList
        .filter((hs) => hs.toFhirResource().active !== false)
        .map((hs) => ({
          value: hs.reference.reference,
          label: hs.name,
          keywords: hs.searchKeywords,
        })),
    [healthcareServicesList]
  )

  const selectedService = useMemo(
    () =>
      value &&
      healthcareServicesList.find(
        (hs) => hs.reference.reference === getReferenceString(value)
      ),
    [healthcareServicesList, value]
  )

  return (
    <Combobox
      options={options}
      onSelect={(referenceString) => {
        const service = healthcareServicesList.find(
          (hs) => hs.reference.reference === referenceString
        )
        if (service) {
          onChange(service.reference)
        }
      }}
    >
      <Button
        variant="outline"
        role="combobox"
        className="w-full justify-between"
        aria-label="Select appointment type"
        {...delegated}
      >
        {selectedService?.name ?? 'Select appointment type…'}
      </Button>
    </Combobox>
  )
}
